import { Fragment, React, css, styled, useEffect, useState, useRef, useTheme } from '../../lib/imports';
import { useSelector } from 'react-redux';
import { Col, ColorProvider, Grid, Heading, Spacing, Statistic } from '../../components';

const styledHeadingBaseStyles = ({ theme }) => css`
  line-height: 20px;
  margin-bottom: ${theme.spacings.byte};
`;

const StyledHeading = styled(Heading)(styledHeadingBaseStyles);
const baseStyles = ({ theme }) => css`
  color: ${theme.colors.white};
  display: flex;
  overflow-x: scroll;
  list-style: none;
  padding: 0;
  margin-bottom: 0;
  ::-webkit-scrollbar {
    display: none;
  }
`;
const CarouselContainer = styled('ol')(baseStyles);

const panelContainerBaseStyles = ({ theme }) => css`
  background: #2c2c2c;
  border-radius: 11px;
  padding: ${theme.spacings.giga};
  margin-bottom: ${theme.spacings.mega};
  display: flex;
  flex-direction: column;
  min-width: 100%;
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const PanelContainer = styled('li')(panelContainerBaseStyles);

const IndicatorContainer = styled('div')(css`
  display: flex;
  justify-content: center;
`);
const bulletStyles = ({ theme }) => css`
  background: ${theme.colors.o300};
  border-radius: 50%;
  padding: ${theme.spacings.byte};
  margin: ${theme.spacings.byte} ${theme.spacings.giga};
  border: none;
`;
const ButtelLabel = styled.div`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;
const BulletBtn = styled('button')(bulletStyles);

const Carousel = ({ data }) => {
  const { strings } = useSelector((state) => state);
  const [scrollProgress, setScrollProgress] = useState(0);
  const theme = useTheme();

  const displayableStats = data.filter((d) => {
    if (Object.keys(d).length > 0) {
      return d;
    }
  });
  const carouselContainerRef = useRef();
  let carouselWidth;

  const pickAccuracyTitle = strings['heatstreak/statistic/pickAccuracy/title'];
  const daysPlayedTitle = strings['heatstreak/statistic/daysPlayed/title'];
  const correctPicksTitle = strings['heatstreak/statistic/correctPicks/title'];

  const carouselBulletText1 = strings['button/heatstreak/panel1'];
  const carouselBulletText2 = strings['button/heatstreak/panel2'];
  const carouselBulletText3 = strings['button/heatstreak/panel3'];
  const bulletTextArray = [carouselBulletText1, carouselBulletText2, carouselBulletText3];

  const handleClick = (i) => {
    let moveAmount;
    if (scrollProgress < i) {
      if (i < displayableStats.length - 1) {
        moveAmount = (carouselWidth + 4 * (displayableStats.length - 1)) * Math.abs(scrollProgress - i);
      } else {
        moveAmount = (carouselWidth + 4 * (displayableStats.length - 1)) * Math.abs(scrollProgress - i);
      }
    } else {
      if (i > 0) {
        moveAmount = -Math.abs((carouselWidth + 4 * (displayableStats.length - 1)) * (scrollProgress - i));
      } else {
        moveAmount = -Math.abs(carouselWidth + 4 * (displayableStats.length - 1)) * (scrollProgress - i);
      }
    }
    if (carouselContainerRef.current) {
      carouselContainerRef.current.scrollLeft += moveAmount;
    }
    setScrollProgress(i);
  };

  const scrollListener = () => {
    if (carouselContainerRef.current) {
      const elm = carouselContainerRef.current;
      const windowScroll = elm.scrollLeft;
      const totalWidth = elm.scrollWidth - carouselWidth;
      const tileSize = totalWidth / data.length;
      const scrolledStep = Math.floor(windowScroll / tileSize);

      if (scrolledStep <= data.length - 1) setScrollProgress(scrolledStep);
    }
  };

  useEffect(() => {
    if (carouselContainerRef.current) {
      carouselWidth = carouselContainerRef.current.clientWidth;
      carouselContainerRef.current.addEventListener('touchmove', scrollListener);
    }

    return () => {
      if (carouselContainerRef.current) {
        carouselContainerRef.current.removeEventListener('touchmove', scrollListener);
      }
    };
  });

  const renderPillIndicators = (pillCount, i) => {
    const selectedPillValue = scrollProgress;
    const isActive = selectedPillValue === i;
    return (
      <Fragment key={i}>
        <ButtelLabel id={`buttonLabel${i}`}>{bulletTextArray[i]}</ButtelLabel>
        <BulletBtn
          style={{ backgroundColor: isActive ? theme.colors.white : theme.colors.white50 }}
          onClick={() => {
            handleClick(i);
          }}
          aria-labelledby={`buttonLabel${i}`}
        />
      </Fragment>
    );
  };

  const renderPanel = (data, index) => {
    let styleMod;
    if (index === 0) {
      styleMod = { marginRight: theme.spacings.bit };
    } else if (index === displayableStats.length - 1) {
      styleMod = { marginLeft: theme.spacings.bit };
    } else {
      styleMod = { marginLeft: theme.spacings.bit, marginRight: theme.spacings.bit };
    }

    return (
      <PanelContainer key={`${data.title}_${index}`} style={{ ...styleMod }}>
        <Spacing sizeBottom='mega'>
          <ColorProvider variant='white'>
            <StyledHeading as='h3' size='mega' noMarginBoth>
              {data.title}
            </StyledHeading>
          </ColorProvider>
        </Spacing>
        <Grid style={{ alignItems: 'start' }}>
          <Col columns='4'>
            <Spacing>
              <Statistic
                titleColor='n800'
                suffixTextSize='tera'
                title={pickAccuracyTitle}
                value={data.pickAccuracy ? `${data.pickAccuracy}` : '0'}
              />
            </Spacing>
          </Col>
          <Col columns='4'>
            <Spacing sizeLeft='mega'>
              <Statistic
                titleColor='n800'
                suffixTextSize='tera'
                title={daysPlayedTitle}
                value={data.daysPlayed ? `${data.daysPlayed}` : '0'}
                suffix=''
              />
            </Spacing>
          </Col>
          <Col columns='4'>
            <Spacing sizeLeft='mega'>
              <Statistic
                titleColor='n800'
                suffixTextSize='tera'
                title={correctPicksTitle}
                value={data.correctPicks ? `${data.correctPicks}` : '0'}
                suffix=''
              />
            </Spacing>
          </Col>
        </Grid>
      </PanelContainer>
    );
  };
  return (
    <>
      <CarouselContainer ref={carouselContainerRef} className='carouselContainer'>
        {displayableStats.map((d, index) => {
          return renderPanel(d, index);
        })}
      </CarouselContainer>
      {displayableStats.length > 1 && (
        <IndicatorContainer target={carouselContainerRef}>
          {displayableStats.map((d, index) => {
            return renderPillIndicators(displayableStats.length, index);
          })}
        </IndicatorContainer>
      )}
    </>
  );
};

Carousel.propTypes = {};

export default Carousel;
